import {SketchRelationKind} from '../sketch/solver/constraint/relation-compatibility/SketchRelationKinds';
import {CSS2DObject} from 'three/examples/jsm/renderers/CSS2DRenderer.js';

/**
 * 几何关系标签对象。
 * 职责：
 * 1. 封装关系标签的标识属性；
 * 2. 管理布局可见性状态；
 * 3. 管理异步渲染版本号。
 */
export class SketchRelationMarkerLabelObject extends CSS2DObject {
    //------ 几何关系标签屏幕偏移 开始------
    /** 标签默认中心点 X。 */
    private static readonly DEFAULT_CENTER_X = 0.5;
    /** 标签默认中心点 Y。 */
    private static readonly DEFAULT_CENTER_Y = 0.5;
    //------ 几何关系标签屏幕偏移 结束------

    /** 标签类型。 */
    public markerType: SketchRelationKind;
    /** 标签键。 */
    public markerKey: string;
    /** 布局可见性。 */
    public layoutVisible: boolean;
    /** 渲染版本号。 */
    private renderVersion: number;

    /**
     * 构造函数。
     * @param element 标签元素。
     * @param markerType 标签类型。
     * @param markerKey 标签键。
     */
    constructor(element: HTMLImageElement, markerType: SketchRelationKind, markerKey: string) {
        super(element);
        this.name = '__geometry_relation_marker__';
        this.markerType = markerType;
        this.markerKey = markerKey;
        this.layoutVisible = true;
        this.renderVersion = 0;
    }

    /**
     * 更新标签标识。
     * @param markerType 标签类型。
     * @param markerKey 标签键。
     */
    public setMarkerIdentity(markerType: SketchRelationKind, markerKey: string): void {
        this.markerType = markerType;
        this.markerKey = markerKey;
    }

    /**
     * 更新布局可见性。
     * @param visible 是否可见。
     */
    public setLayoutVisible(visible: boolean): void {
        this.layoutVisible = visible;
    }

    //------ 几何关系标签屏幕偏移 开始------
    /**
     * 按屏幕像素设置标签相对锚点的偏移。
     * 说明：
     * 1. CSS2DRenderer 会基于 `center` 计算 DOM 的百分比平移；
     * 2. 将像素偏移换算成中心点偏移后，标签锚点仍保持在 3D 几何中点；
     * 3. 这样避免使用世界单位移动标签导致透视投影后出现超大屏幕偏移。
     * @param offsetX 标签相对锚点的屏幕 X 偏移，正值向右。
     * @param offsetY 标签相对锚点的屏幕 Y 偏移，正值向下。
     * @param iconSizePx 标签图标尺寸，单位像素。
     */
    public setScreenPixelOffset(offsetX: number, offsetY: number, iconSizePx: number): void {
        if (iconSizePx <= 0) {
            this.center.set(
                SketchRelationMarkerLabelObject.DEFAULT_CENTER_X,
                SketchRelationMarkerLabelObject.DEFAULT_CENTER_Y,
            );
            return;
        }

        this.center.set(
            SketchRelationMarkerLabelObject.DEFAULT_CENTER_X - offsetX / iconSizePx,
            SketchRelationMarkerLabelObject.DEFAULT_CENTER_Y - offsetY / iconSizePx,
        );
    }
    //------ 几何关系标签屏幕偏移 结束------

    /**
     * 递增渲染版本号。
     * @returns 递增后的渲染版本号。
     */
    public bumpRenderVersion(): number {
        this.renderVersion += 1;
        return this.renderVersion;
    }

    /**
     * 判断版本号是否仍为当前版本。
     * @param expectedVersion 预期版本号。
     * @returns 是否匹配当前版本。
     */
    public isCurrentRenderVersion(expectedVersion: number): boolean {
        return this.renderVersion === expectedVersion;
    }
}
